// ============================================================================
// SYUT CLIENT SELECT
// ============================================================================
// Domain wrapper around SyutSelect pre-configured to search the Client master
// via clientService. Scales to any number of clients (server-side searchTerm).
// Usage:
//   <SyutClientSelect value={formData.clientId} onChange={(id) => ...} />

import React, { useCallback } from 'react';
import { SyutSelect } from '../SyutSelect';

import { clientService } from 'api/services/clientService';
import { useResolvedSeedOptions } from './useResolvedSeedOptions';

/** One place that builds a client's label, so the dropdown and the resolved
 *  selected value can never disagree about how a client is written. */
const clientLabel = (c: any) => `${c.code} - ${c.name}`;

export interface SyutClientSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Extra options (e.g. preloaded list) used for label resolution in edit mode. */
  seedOptions?: { value: string; label: string }[];
  /** Include inactive clients in results (default: false) */
  includeInactive?: boolean;
  /** Rows per server query (default: 500). On dropdown open, this many rows
   *  are loaded; on typed search, this is the page size for results. */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutClientSelect: React.FC<SyutClientSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder = 'Type 3 letters to search clients...',
  size = 'sm',
  seedOptions,
  includeInactive = false,
  pageSize = 500,
  disabled,
}) => {
  // A selected client outside the loaded page would otherwise render blank —
  // see useResolvedSeedOptions for the full account.
  const fetchOption = useCallback(async (id: string) => {
    const res = await clientService.getById(id);
    const c: any = res?.data;
    return c ? { value: String(c.id), label: clientLabel(c) } : null;
  }, []);

  const effectiveSeedOptions = useResolvedSeedOptions(value, seedOptions, fetchOption);

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const res = await clientService.getAll({
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          includeInactive,
        });
        return res.data.map((c: any) => ({ value: c.id, label: clientLabel(c) }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={effectiveSeedOptions}
      debounceDelay={300}
      minSearchLength={3}
      isClearable={isClearable}
      size={size}
      placeholder={placeholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutClientSelect;
